import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select,
  MenuItem, FormControl, InputLabel, Alert, IconButton, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Radio, RadioGroup, FormControlLabel, FormLabel, Divider,
  Tooltip, Stack, Menu, ListItemIcon, ListItemText, useMediaQuery,
  Autocomplete, Switch, ListSubheader,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ArrowBack, Edit, Delete, Add, Warning, Star, PictureAsPdf,
  Map as MapIcon, Close, CameraAlt, Assignment, Schedule,
  HealingOutlined, EventNote, PhotoLibrary, FactCheck,
  PhotoCamera, Image as ImageIcon, MoreVert,
} from '@mui/icons-material';
import { treesAPI, inspectionsAPI, measuresAPI, photosAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getTreeFilterOptions, invalidateTreeFilterOptions } from '../services/treeFilterOptions';
import EmptyState from '../components/EmptyState';
import {
  ZUSTAND_OPTIONS, VITALITAET_OPTIONS, ENTWICKLUNG_OPTIONS, SICHERHEIT_OPTIONS,
  VERKEHRSSICHERHEIT_OPTIONS, getVerkehrssicherheitColor, KONTROLL_INTERVALL_OPTIONS, ANZAHL_STAEMME_OPTIONS,
  FUNKTION_OPTIONS,
  MEASURE_TYPE_OPTIONS,
  MEASURE_TYPE_GROUPS,
  SCHADMERKMAL_KRONE_OPTIONS,
  SCHADMERKMAL_STAMM_OPTIONS,
  SCHADMERKMAL_STAMMFUSS_OPTIONS,
  getMerkmalBeschreibung,
  getMeasureBeschreibung,
} from '../data/treeFieldOptions';

const damagesLocalAPI = {
  create: (data: any) => api.post('/damages', data),
  update: (id: string, data: any) => api.put(`/damages/${id}`, data),
  bestaetigen: (id: string, body: any) => api.put(`/damages/${id}/bestaetigen`, body),
  delete: (id: string) => api.delete(`/damages/${id}`),
};

const PRIORITY_COLORS: Record<string, any> = { dringend: 'error', hoch: 'warning', mittel: 'info', niedrig: 'default' };
const STATUS_COLORS: Record<string, any> = { offen: 'warning', in_bearbeitung: 'info', erledigt: 'success', behoben: 'success', abgebrochen: 'default' };
const STATUS_LABELS: Record<string, string> = { offen: 'Offen', in_bearbeitung: 'In Bearbeitung', erledigt: 'Erledigt', behoben: 'Behoben', abgebrochen: 'Abgebrochen' };

// Schadmerkmale-Mapping: Schadbereich → FLL-konforme Auswahlliste (BUG-018)
// Quelle: FLL-Baumkontrollrichtlinie. Listen sind in treeFieldOptions.ts gepflegt.
// "Stammfuß" deckt laut FLL auch Wurzel und Baumumfeld ab. Der Alias "Wurzel"
// bleibt für Backwards-Compat mit Alt-Daten erhalten und teilt sich die Liste.
const SCHAD_BEREICHE: Record<string, string[]> = {
  'Krone':    SCHADMERKMAL_KRONE_OPTIONS,
  'Stamm':    SCHADMERKMAL_STAMM_OPTIONS,
  'Stammfuß': SCHADMERKMAL_STAMMFUSS_OPTIONS,
  'Wurzel':   SCHADMERKMAL_STAMMFUSS_OPTIONS,
};

// Relative Zeitangabe für "Letzte Änderung" – menschlicher als reines Datum
function getRelativeTime(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'gestern';
  if (days < 7) return `vor ${days} Tagen`;
  if (days < 30) return `vor ${Math.floor(days / 7)} Wo.`;
  const months = Math.floor(days / 30);
  if (months < 12) return `vor ${months} Mon.`;
  return `vor ${Math.floor(days / 365)} J.`;
}

const InfoRow = ({ label, value }: { label: string; value: any }) => (
  <Box sx={{ display: 'flex', py: 0.7 }}>
    <Typography variant="body2" sx={{ width: 150, flexShrink: 0, color: 'text.secondary', fontWeight: 500, fontSize: '0.82rem' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{value || '–'}</Typography>
  </Box>
);

// Radio selector component
const RadioSelect = ({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (v: string) => void }) => (
  <Box sx={{ mb: 2 }}>
    <FormLabel sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5, display: 'block' }}>{label}</FormLabel>
    <RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(opt => (
        <FormControlLabel key={opt} value={opt} control={<Radio size="small" />}
          label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{opt}</Typography>}
          sx={{ mr: 2 }} />
      ))}
    </RadioGroup>
  </Box>
);

// PDF for Kontrolle nach FLL
function generateControlPDF(control: any, tree: any) {
  const w = window.open('', '_blank');
  if (!w) return;

  const radio = (label: string, options: string[], selected: string) =>
    options.map(o => `<span style="margin-right:12px">${o === selected ? '●' : '○'} ${o}</span>`).join('');

  w.document.write(`<!DOCTYPE html><html><head><title>Baumkontrolle ${tree.baumnummer}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 30px; color: #333; max-width: 800px; margin: 0 auto; font-size: 13px; }
      h1 { color: #4a7c59; font-size: 20px; margin: 0 0 4px; }
      h2 { color: #4a7c59; font-size: 14px; margin: 20px 0 8px; border-bottom: 2px solid #4a7c59; padding-bottom: 4px; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #4a7c59; padding-bottom: 12px; margin-bottom: 16px; }
      .header-right { text-align: right; color: #666; }
      .row { display: flex; gap: 20px; margin-bottom: 6px; }
      .field { flex: 1; }
      .field-label { color: #888; font-size: 11px; margin-bottom: 2px; }
      .field-value { font-weight: 600; }
      .radio-row { margin: 6px 0; }
      .radio-label { font-weight: 600; min-width: 180px; display: inline-block; }
      .section { border: 1px solid #ddd; border-radius: 6px; padding: 12px; margin: 8px 0; }
      .section-title { font-weight: 700; color: #4a7c59; margin-bottom: 8px; }
      .befund { background: #f9f9f9; padding: 8px; border-radius: 4px; margin: 4px 0; min-height: 20px; }
      .footer { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 20px; display: flex; justify-content: space-between; }
      .sig-line { border-top: 1px solid #333; width: 200px; margin-top: 60px; padding-top: 4px; text-align: center; color: #666; }
      @media print { body { padding: 15px; } }
    </style></head><body>

    <div class="header">
      <div>
        <img src="${window.location.origin}/logo.png" alt="Baumwächter" style="height:44px; display:block; margin-bottom:8px" />
        <h1 style="margin:0 0 4px">BAUMKONTROLLE</h1>
        <div style="color:#666">nach FLL-Richtlinien</div>
        <div style="color:#666; margin-top:4px">Kontrolleur: ${control.controller_name || '–'}</div>
      </div>
      <div class="header-right">
        <div style="font-size:16px; font-weight:700">${control.control_date ? new Date(control.control_date).toLocaleDateString('de-DE') : '–'}</div>
        <div>Kontrolle Nr. ${control.control_number || '–'}</div>
      </div>
    </div>

    <div class="row">
      <div class="field"><div class="field-label">Baum-Nr</div><div class="field-value">${tree.baumnummer || '–'}</div></div>
      <div class="field"><div class="field-label">Baumart</div><div class="field-value">${tree.baumart || '–'}</div></div>
      <div class="field"><div class="field-label">Standort</div><div class="field-value">${[tree.strasse, tree.ort].filter(Boolean).join(', ') || '–'}</div></div>
    </div>

    <h2>Baummaße</h2>
    <div class="row">
      <div class="field"><div class="field-label">Baumhöhe</div><div class="field-value">${control.baumhoehe ? control.baumhoehe + ' m' : '–'}</div></div>
      <div class="field"><div class="field-label">Stamm-Ø (1m Höhe)</div><div class="field-value">${control.stammdurchmesser ? control.stammdurchmesser + ' cm' : '–'}</div></div>
      <div class="field"><div class="field-label">Kronen-Ø</div><div class="field-value">${control.kronendurchmesser ? control.kronendurchmesser + ' m' : '–'}</div></div>
    </div>

    <h2>Bewertung</h2>
    <div class="radio-row"><span class="radio-label">Entwicklungsphase:</span> ${radio('', ['Jugendphase', 'Reifephase', 'Alterungsphase', 'Abgangsphase'], control.entwicklungsphase || '')}</div>
    <div class="radio-row"><span class="radio-label">Vitalitätsstufe:</span> ${radio('', ['0 – vital', '1 – leicht geschwächt', '2 – geschwächt', '3 – stark geschwächt'], control.vitalitaetsstufe || '')}</div>
    <div class="radio-row"><span class="radio-label">Zustand:</span> ${radio('', ['gut', 'leichte Mängel', 'erhebliche Mängel', 'Gefahr im Verzug'], control.zustand || '')}</div>
    <div class="radio-row"><span class="radio-label">Kontrollintervall:</span> ${radio('', ['halbjährlich', 'jährlich', '2-jährlich', '3-jährlich'], control.kontroll_intervall || '')}</div>
    <div class="radio-row"><span class="radio-label">Sicherheitserwartung:</span> ${radio('', ['hoch', 'niedrig'], control.sicherheitserwartung || '')}</div>

    <h2>Defektsymptome & Verkehrssicherheit</h2>

    <div class="section">
      <div class="section-title">Krone</div>
      <div class="field-label">Befund:</div>
      <div class="befund">${control.krone_befund || '–'}</div>
      <div style="margin-top:4px"><span class="field-label">Verkehrssicherheit: </span>${radio('', ['gegeben', 'nicht gegeben', 'eingeschränkt gegeben'], control.krone_verkehr || '')}</div>
    </div>

    <div class="section">
      <div class="section-title">Stamm</div>
      <div class="field-label">Befund:</div>
      <div class="befund">${control.stamm_befund || '–'}</div>
      <div style="margin-top:4px"><span class="field-label">Verkehrssicherheit: </span>${radio('', ['gegeben', 'nicht gegeben', 'eingeschränkt gegeben'], control.stamm_verkehr || '')}</div>
    </div>

    <div class="section">
      <div class="section-title">Wurzeln</div>
      <div class="field-label">Befund:</div>
      <div class="befund">${control.wurzeln_befund || '–'}</div>
      <div style="margin-top:4px"><span class="field-label">Verkehrssicherheit: </span>${radio('', ['gegeben', 'nicht gegeben', 'eingeschränkt gegeben'], control.wurzeln_verkehr || '')}</div>
    </div>

    ${control.handlungsbedarf ? `<h2>Handlungsbedarf</h2><div class="befund">${control.handlungsbedarf}</div>` : ''}
    ${control.durchfuehrungszeitraum ? `<h2>Durchführungszeitraum</h2><div class="befund">${control.durchfuehrungszeitraum}</div>` : ''}
    ${control.control_text ? `<h2>Weitere Bemerkungen</h2><div class="befund">${control.control_text}</div>` : ''}

    <div class="footer">
      <div>
        <div class="sig-line">Unterschrift Kontrolleur</div>
        <div style="text-align:center; margin-top:4px">${control.controller_name || ''}</div>
      </div>
      <div>
        <div class="sig-line">Stempel</div>
      </div>
    </div>

    <div style="margin-top:20px; color:#bbb; font-size:10px; text-align:center">
      Baumkontrolle_${tree.baumnummer}_${control.control_date ? new Date(control.control_date).toISOString().split('T')[0] : 'undatiert'} · Erstellt mit Baumwächter
    </div>
  </body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 400);
}

export default function TreeDetail() {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state as any) || {};
  const cameFromLV = fromState.from === 'lv' && !!fromState.lvId;
  const cameFromMap = fromState.from === 'map';
  const cameFromActivity = fromState.from === 'activity';  // BUG-016: Zurück zur Historie
  const { canEdit, isAdmin, canReportDamage, user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState<HTMLElement | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tree, setTree] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const [damageDialog, setDamageDialog] = useState(false);
  const [bestaetigenDialog, setBestaetigenDialog] = useState<string | null>(null); // Schaden-ID
  const [bestaetigenNotiz, setBestaetigenNotiz] = useState('');
  const [damageMenu, setDamageMenu] = useState<{ anchor: HTMLElement; d: any } | null>(null); // mobiles ⋯-Menü
  const [measureDialog, setMeasureDialog] = useState(false);
  const [measureError, setMeasureError] = useState('');
  const [controlDialog, setControlDialog] = useState(false);
  const [editFromControl, setEditFromControl] = useState(false); // BUG-027: merkt, dass „Baum bearbeiten" aus der Kontrolle geöffnet wurde
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [damageForm, setDamageForm] = useState({ schadbereich: '', damage_type: '', severity: 'Mittel', location_on_tree: '', description: '', priority: 'mittel', discovered_date: new Date().toISOString().split('T')[0] });
  const EMPTY_MEASURE_FORM = { measure_type: '', description: '', planned_date: '', status: 'offen', assigned_to: '', damage_id: null as string | null, cost: '', completed_date: '', completion_notes: '' };
  const [editingMeasureId, setEditingMeasureId] = useState<string | null>(null); // null = anlegen, gesetzt = bearbeiten
  const [measureForm, setMeasureForm] = useState<{ measure_type: string; description: string; planned_date: string; status: string; assigned_to: string; damage_id: string | null; cost: string; completed_date: string; completion_notes: string }>(EMPTY_MEASURE_FORM);

  // User-Liste für Bearbeiter-Dropdown (FEAT-011)
  const [users, setUsers] = useState<any[]>([]);
  useEffect(() => {
    // /users ist admin-only → für die Bearbeiter-Auswahl den schlanken
    // /users/selectable-Endpoint nutzen (admin + erfasser dürfen zuweisen).
    api.get('/users/selectable')
      .then(res => setUsers(res.data || []))
      .catch(() => {/* still – evtl. kein Recht */});
  }, []);

  // Mark-Done-Dialog: wenn Maßnahme mit damage_id erledigt wird, fragen ob Schaden auch behoben
  const [confirmDamageBehoben, setConfirmDamageBehoben] = useState<{ measureId: string; damageId: string } | null>(null);
  const [suggestMeasure, setSuggestMeasure] = useState<{ description: string } | null>(null); // FEAT-029

  // FLL Control form
  const [controlForm, setControlForm] = useState<{
    control_date: string; control_text: string;
    entwicklungsphase: string; vitalitaetsstufe: string; zustand: string;
    kontroll_intervall: string; sicherheitserwartung: string;
    baumhoehe: string; stammdurchmesser: string; kronendurchmesser: string;
    // Schadmerkmale als Listen (FLL, BUG-018) – werden beim Speichern in
    // den jeweiligen Befund-Text vorangestellt.
    krone_merkmale: string[]; krone_befund: string; krone_verkehr: string;
    stamm_merkmale: string[]; stamm_befund: string; stamm_verkehr: string;
    wurzeln_merkmale: string[]; wurzeln_befund: string; wurzeln_verkehr: string;
    handlungsbedarf: string;
    durchfuehrungszeitraum: string;
    verkehrssicherheit: string;
  }>({
    control_date: new Date().toISOString().split('T')[0],
    control_text: '',
    entwicklungsphase: '', vitalitaetsstufe: '', zustand: '', kontroll_intervall: '', sicherheitserwartung: '',
    baumhoehe: '', stammdurchmesser: '', kronendurchmesser: '',
    krone_merkmale: [],   krone_befund: '',   krone_verkehr: '',
    stamm_merkmale: [],   stamm_befund: '',   stamm_verkehr: '',
    wurzeln_merkmale: [], wurzeln_befund: '', wurzeln_verkehr: '',
    handlungsbedarf: '',
    durchfuehrungszeitraum: '',
    verkehrssicherheit: '',
  });

  const resetControlForm = () => setControlForm({
    control_date: new Date().toISOString().split('T')[0], control_text: '',
    entwicklungsphase: '', vitalitaetsstufe: '', zustand: '', kontroll_intervall: '', sicherheitserwartung: '',
    baumhoehe: '', stammdurchmesser: '', kronendurchmesser: '',
    krone_merkmale: [],   krone_befund: '',   krone_verkehr: '',
    stamm_merkmale: [],   stamm_befund: '',   stamm_verkehr: '',
    wurzeln_merkmale: [], wurzeln_befund: '', wurzeln_verkehr: '',
    handlungsbedarf: '',
    durchfuehrungszeitraum: '',
    verkehrssicherheit: '',
  });

  const loadTree = async () => {
    try { const res = await treesAPI.getById(id!); setTree(res.data); setEditData(res.data); }
    catch { setError('Baum konnte nicht geladen werden'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadTree(); }, [id]);

  // Autocomplete-Optionen für Baumart / Ort / Strasse / Eigentümer (IMP-009/010)
  // Aus zentralem Cache – lädt nur 1x pro Session.
  const [autocompleteOptions, setAutocompleteOptions] = useState<{ baumarten: string[]; orte: string[]; strassen: string[]; eigentuemer: string[] }>({ baumarten: [], orte: [], strassen: [], eigentuemer: [] });
  useEffect(() => {
    getTreeFilterOptions().then(opts => setAutocompleteOptions({
      baumarten: opts.baumarten, orte: opts.orte, strassen: opts.strassen, eigentuemer: opts.eigentuemer,
    }));
  }, []);

  const schadmerkmale = damageForm.schadbereich ? SCHAD_BEREICHE[damageForm.schadbereich] || [] : [];

  // Handlers
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload: any = { ...editData };
      // Baumgruppen-Daten normalisieren (FEAT-015)
      if (payload.is_baumgruppe) {
        payload.gruppe_anzahl = payload.gruppe_anzahl ? parseInt(payload.gruppe_anzahl) : null;
        if (Array.isArray(payload.gruppe_arten) && payload.gruppe_arten.length > 0) {
          payload.baumart = payload.gruppe_arten.join(', ');
        }
      } else {
        payload.gruppe_anzahl = null;
        payload.gruppe_arten = null;
      }
      await treesAPI.update(id!, payload);
      invalidateTreeFilterOptions();
      setEditDialog(false);
      loadTree();
      // BUG-027: Wenn „Baum bearbeiten" aus der Kontrolle heraus geöffnet wurde,
      // nach dem Speichern zurück in den Kontroll-Dialog springen.
      if (editFromControl) { setEditFromControl(false); setControlDialog(true); }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Fehler');
    } finally {
      setSaving(false);
    }
  };
  const handleCreateDamage = async () => { setSaving(true); try { await damagesLocalAPI.create({ tree_id: id, damage_type: damageForm.damage_type || damageForm.schadbereich, severity: damageForm.severity, location_on_tree: damageForm.schadbereich, description: [damageForm.location_on_tree, damageForm.description].filter(Boolean).join(' – '), priority: damageForm.priority, discovered_date: damageForm.discovered_date }); setDamageDialog(false); setDamageForm({ schadbereich: '', damage_type: '', severity: 'Mittel', location_on_tree: '', description: '', priority: 'mittel', discovered_date: new Date().toISOString().split('T')[0] }); loadTree(); } catch (e: any) { setError(e.response?.data?.error || 'Fehler'); } finally { setSaving(false); } };
  const handleDamageStatus = async (did: string, status: string) => { try { await damagesLocalAPI.update(did, { status }); loadTree(); } catch {} };
  const handleResetBestaetigung = async (did: string) => { try { await damagesLocalAPI.bestaetigen(did, { clear: true }); loadTree(); } catch (e: any) { setError(e.response?.data?.error || 'Fehler'); } };
  const submitBestaetigung = async () => {
    if (!bestaetigenDialog) return;
    try {
      await damagesLocalAPI.bestaetigen(bestaetigenDialog, { notiz: bestaetigenNotiz });
      setBestaetigenDialog(null); setBestaetigenNotiz(''); loadTree();
    } catch (e: any) { setError(e.response?.data?.error || 'Fehler'); }
  };
  const handleDeleteDamage = async (did: string) => { if (!window.confirm('Schaden löschen?')) return; try { await damagesLocalAPI.delete(did); loadTree(); } catch {} };
  // Maßnahme erstellen (ggf. mit Bearbeiter + Schaden-Verknüpfung)
  const handleCreateMeasure = async () => {
    setSaving(true);
    setMeasureError('');
    try {
      const payload: any = { ...measureForm };
      // Leere Strings zu null normalisieren
      if (!payload.assigned_to) payload.assigned_to = null;
      if (!payload.damage_id) payload.damage_id = null;
      if (!payload.planned_date) payload.planned_date = null;
      if (!payload.completed_date) payload.completed_date = null;
      if (payload.cost === '' || payload.cost == null) payload.cost = null;
      if (editingMeasureId) {
        await measuresAPI.update(editingMeasureId, payload);
      } else {
        payload.tree_id = id;
        await measuresAPI.create(payload);
      }
      setMeasureDialog(false);
      setEditingMeasureId(null);
      setMeasureForm(EMPTY_MEASURE_FORM);
      loadTree();
    } catch (e: any) {
      // Duplikat (409) o.ä. direkt im Dialog anzeigen, Eingaben bleiben erhalten
      setMeasureError(e.response?.data?.error || 'Maßnahme konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  // Status einer Maßnahme ändern. Bei "erledigt" + verknüpftem Schaden:
  // Bestätigungs-Dialog ob auch der Schaden als "behoben" markiert werden soll.
  const handleMeasureStatus = async (mid: string, status: string) => {
    try {
      const measure = measures.find((m: any) => m.id === mid);
      const completed_date = status === 'erledigt' ? new Date().toISOString().split('T')[0] : undefined;
      await measuresAPI.update(mid, { status, completed_date });

      if (status === 'erledigt' && measure?.damage_id) {
        // Verknüpfter Schaden: User fragen
        setConfirmDamageBehoben({ measureId: mid, damageId: measure.damage_id });
      } else {
        loadTree();
      }
    } catch {}
  };

  // Bestätigung: Schaden zur erledigten Maßnahme auch behoben markieren?
  const confirmAndCloseDamage = async (alsoCloseDamage: boolean) => {
    if (!confirmDamageBehoben) return;
    if (alsoCloseDamage) {
      try { await damagesLocalAPI.update(confirmDamageBehoben.damageId, { status: 'behoben' }); } catch {}
    }
    setConfirmDamageBehoben(null);
    loadTree();
  };

  // "Maßnahme aus Schaden anlegen" – Dialog mit vorausgefüllten Werten öffnen
  const openMeasureFromDamage = (damage: any) => {
    setEditingMeasureId(null);
    setMeasureForm({
      ...EMPTY_MEASURE_FORM,
      description: `Bezugnehmend auf Schaden: ${damage.damage_type}${damage.location_on_tree ? ` (${damage.location_on_tree})` : ''}`,
      damage_id: damage.id,
    });
    setMeasureError('');
    setMeasureDialog(true);
  };

  // Bestehende Maßnahme bearbeiten – Dialog mit ihren Werten vorbefüllen
  const openEditMeasure = (m: any) => {
    setEditingMeasureId(m.id);
    setMeasureForm({
      measure_type: m.measure_type || '',
      description: m.description || '',
      planned_date: m.planned_date ? String(m.planned_date).slice(0, 10) : '',
      status: m.status || 'offen',
      assigned_to: m.assigned_to || '',
      damage_id: m.damage_id || null,
      cost: m.cost != null ? String(m.cost) : '',
      completed_date: m.completed_date ? String(m.completed_date).slice(0, 10) : '',
      completion_notes: m.completion_notes || '',
    });
    setMeasureError('');
    setMeasureDialog(true);
  };
  const handleDeleteMeasure = async (mid: string) => { if (!window.confirm('Maßnahme löschen?')) return; try { await measuresAPI.delete(mid); loadTree(); } catch {} };
  const handleDelete = async () => { if (!window.confirm('Baum wirklich löschen?')) return; try { await treesAPI.delete(id!); navigate('/trees'); } catch {} };

  // Beim Hinzufügen eines FLL-Merkmals wird seine Beschreibung automatisch
  // in das Befund-Freitextfeld vorangestellt – wenn sie nicht schon drin ist.
  // Beim Entfernen wird die zugehörige Beschreibungszeile ebenfalls entfernt.
  // (BUG-019)
  const handleMerkmalChange = (
    bereich: 'krone' | 'stamm' | 'stammfuss',
    oldList: string[],
    newList: string[],
    merkmalKey: 'krone_merkmale' | 'stamm_merkmale' | 'wurzeln_merkmale',
    befundKey: 'krone_befund' | 'stamm_befund' | 'wurzeln_befund',
  ) => {
    const oldSet = new Set(oldList);
    const newSet = new Set(newList);
    const added = newList.filter(v => !oldSet.has(v));
    const removed = oldList.filter(v => !newSet.has(v));

    let befundText = (controlForm[befundKey] || '') as string;

    // Entfernte Merkmale: zugehörige Zeile aus dem Befund-Text raus
    for (const v of removed) {
      const beschr = getMerkmalBeschreibung(bereich, v);
      // Entferne die Zeile mit dieser Beschreibung (egal ob am Anfang/Mitte/Ende)
      befundText = befundText
        .split('\n')
        .filter(line => line.trim() !== beschr.trim())
        .join('\n')
        .trim();
    }

    // Hinzugefügte Merkmale: Beschreibung als neue Zeile anhängen
    // (nur wenn nicht schon drin – User könnte sie selbst getippt haben)
    for (const v of added) {
      const beschr = getMerkmalBeschreibung(bereich, v);
      if (!befundText.split('\n').some(line => line.trim() === beschr.trim())) {
        befundText = befundText ? befundText + '\n' + beschr : beschr;
      }
    }

    setControlForm({
      ...controlForm,
      [merkmalKey]: newList,
      [befundKey]: befundText,
    });
  };

  // Render-Helper für die Option im Dropdown: zeigt den Kurz-Wert fett +
  // die ausführliche FLL-Beschreibung als kleinen grauen Hilfetext darunter.
  const renderMerkmalOption = (bereich: 'krone' | 'stamm' | 'stammfuss') => (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: any },
    option: string,
  ) => {
    const beschr = getMerkmalBeschreibung(bereich, option);
    const showBeschr = beschr !== option;
    // key separat extrahieren – darf laut React 19+ nicht via spread übergeben werden
    const { key, ...rest } = props;
    return (
      <li key={key} {...rest}>
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{option}</Typography>
          {showBeschr && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', mt: 0.2 }}>
              {beschr}
            </Typography>
          )}
        </Box>
      </li>
    );
  };

  const handleCreateControl = async () => {
    setSaving(true);
    try {
      // Seit BUG-019 werden die FLL-Beschreibungen schon beim Auswählen via
      // handleMerkmalChange in den Befund-Freitext eingefügt. Hier nur noch die
      // _merkmale-Arrays rausfiltern (DB hat keine solchen Spalten).
      const { krone_merkmale, stamm_merkmale, wurzeln_merkmale, ...rest } = controlForm;
      const data = { tree_id: id, ...rest };
      await inspectionsAPI.create(data);

      // Schäden aus der Kontrolle automatisch am Baum anlegen: je Sektion (Krone/Stamm/
      // Wurzeln) mit Schadmerkmal oder Befund ein tree_damage. Priorität aus der
      // sektionsbezogenen Verkehrssicherheit abgeleitet.
      const sektionen: { loc: string; merkmale: any; befund: string; verkehr: string }[] = [
        { loc: 'Krone',   merkmale: krone_merkmale,   befund: controlForm.krone_befund,   verkehr: controlForm.krone_verkehr },
        { loc: 'Stamm',   merkmale: stamm_merkmale,   befund: controlForm.stamm_befund,   verkehr: controlForm.stamm_verkehr },
        { loc: 'Wurzeln', merkmale: wurzeln_merkmale, befund: controlForm.wurzeln_befund, verkehr: controlForm.wurzeln_verkehr },
      ];
      const kontrollDatum = controlForm.control_date ? new Date(controlForm.control_date).toLocaleDateString('de-DE') : '';
      // „ohne besonderen Befund" (o.b.B.) & Co. bedeuten KEINEN Schaden → nicht anlegen.
      const isKeinBefund = (t: string) => {
        const x = (t || '').trim().toLowerCase();
        return x === '' || x.startsWith('ohne besonderen befund') || x.startsWith('o.b.b') || x === 'obb'
          || x.startsWith('ohne befund') || x.startsWith('kein befund') || x.startsWith('keine mängel') || x.startsWith('unauffällig');
      };
      for (const s of sektionen) {
        const realMerkmale = (Array.isArray(s.merkmale) ? s.merkmale : []).filter((m: string) => !isKeinBefund(m));
        const hasMerkmal = realMerkmale.length > 0;
        // Befund-Freitext ohne die „o.b.B."-Zeilen betrachten.
        const befundLines = (s.befund || '').split('\n').map((l: string) => l.trim()).filter((l: string) => l && !isKeinBefund(l));
        const hasBefund = befundLines.length > 0;
        if (!hasMerkmal && !hasBefund) continue;
        const v = (s.verkehr || '').toLowerCase();
        const priority = v.includes('nicht gegeben') ? 'hoch' : v.includes('eingeschränkt') ? 'mittel' : 'niedrig';
        const herkunft = kontrollDatum ? `Aus Baumkontrolle vom ${kontrollDatum}` : 'Aus Baumkontrolle';
        try {
          await damagesLocalAPI.create({
            tree_id: id,
            damage_type: hasMerkmal ? realMerkmale.join(', ') : `Befund ${s.loc}`,
            description: [befundLines.join('\n') || realMerkmale.join(', '), herkunft].filter(Boolean).join(' · '),
            location_on_tree: s.loc,
            priority,
          });
        } catch { /* Schaden-Anlage fehlgeschlagen – Kontrolle bleibt trotzdem gespeichert */ }
      }

      setControlDialog(false);
      // FEAT-029: Werte vor dem Reset sichern, um ggf. eine Maßnahme vorzuschlagen.
      const nichtSicher = controlForm.verkehrssicherheit === 'Nicht gegeben';
      const bedarf = (controlForm.handlungsbedarf || '').trim();
      resetControlForm();
      loadTree();
      if (nichtSicher && canEdit) {
        const heute = new Date().toLocaleDateString('de-DE');
        setSuggestMeasure({
          description: `Aus Baumkontrolle vom ${heute}: Verkehrssicherheit „Nicht gegeben".${bedarf ? ` ${bedarf}` : ''}`,
        });
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const openControlDialog = () => {
    // Pre-fill from tree data
    setControlForm(prev => ({
      ...prev,
      baumhoehe: tree.hoehe || '',
      stammdurchmesser: tree.stdm || '',
      kronendurchmesser: tree.kdm || '',
      entwicklungsphase: tree.entwicklun || '',
      vitalitaetsstufe: tree.vitalitaet || '',
      sicherheitserwartung: tree.sicherheit || '',
    }));
    setControlDialog(true);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => { const files = e.target.files; if (!files || files.length === 0) return; setUploading(true); try { for (let i = 0; i < files.length; i++) { await photosAPI.upload(id!, files[i]); } loadTree(); } catch { setError('Fehler beim Hochladen'); } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; if (cameraInputRef.current) cameraInputRef.current.value = ''; } };
  const handleDeletePhoto = async (photoId: string) => { if (!window.confirm('Foto löschen?')) return; try { await photosAPI.delete(photoId); loadTree(); } catch {} };

  const handlePDFExport = () => {
    const w = window.open('', '_blank'); if (!w) return;
    const damages = tree.damages || [], measures = tree.measures || [];
    w.document.write(`<!DOCTYPE html><html><head><title>Baum ${tree.baumnummer}</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#333;max-width:800px;margin:0 auto}h1{color:#4a7c59;border-bottom:3px solid #4a7c59;padding-bottom:8px}h2{color:#4a7c59;margin-top:24px;font-size:16px}table{width:100%;border-collapse:collapse;margin:8px 0}td,th{border:1px solid #ddd;padding:6px 10px;text-align:left;font-size:13px}th{background:#f5f7f5;font-weight:600}.g{display:grid;grid-template-columns:1fr 1fr;gap:2px}.g div{display:flex;padding:5px 10px;border-bottom:1px solid #f0f0f0;font-size:13px}.g .l{color:#888;min-width:150px}@media print{body{padding:20px}}</style></head><body><h1>Baum ${tree.baumnummer}</h1><p style="color:#888;font-size:13px">${tree.baumart||''} · ${tree.ort||''}, ${tree.strasse||''}</p><h2>Grundinformationen</h2><div class="g">${[['Baumart',tree.baumart],['Höhe',tree.hoehe?tree.hoehe+'m':'–'],['Straße',tree.strasse],['Ort',tree.ort],['Zustand',tree.zustand],['Sicherheit',tree.sicherheit],['Stammdurchmesser',tree.stdm?tree.stdm+'cm':'–'],['Kronendurchmesser',tree.kdm?tree.kdm+'m':'–'],['Pflanzjahr',tree.pflanzjahr],['Eigentümer',tree.eigentueme]].map(([l,v])=>`<div><span class="l">${l}:</span>${v||'–'}</div>`).join('')}</div>${damages.length?`<h2>Schäden (${damages.length})</h2><table><tr><th>Bereich</th><th>Merkmal</th><th>Priorität</th><th>Status</th><th>Beschreibung</th><th>Geprüft</th></tr>${damages.map((d:any)=>`<tr><td>${d.location_on_tree||'–'}</td><td>${d.damage_type||'–'}</td><td>${d.priority||'–'}</td><td>${d.status||'–'}</td><td>${d.description||'–'}</td><td>${d.bestaetigt_am?`${d.bestaetigt_von_name||''} am ${new Date(d.bestaetigt_am).toLocaleDateString('de-DE')}${d.bestaetigt_notiz?` – ${d.bestaetigt_notiz}`:''}`:'–'}</td></tr>`).join('')}</table>`:''}${measures.length?`<h2>Maßnahmen (${measures.length})</h2><table><tr><th>Typ</th><th>Status</th><th>Geplant</th><th>Erledigt</th><th>Beschreibung</th></tr>${measures.map((m:any)=>`<tr><td>${m.measure_type||'–'}</td><td>${m.status||'–'}</td><td>${m.planned_date?new Date(m.planned_date).toLocaleDateString('de-DE'):'–'}</td><td>${m.completed_date?new Date(m.completed_date).toLocaleDateString('de-DE'):'–'}</td><td>${m.description||'–'}</td></tr>`).join('')}</table>`:''}${tree.bemerkung?`<h2>Bemerkungen</h2><p style="font-size:13px">${tree.bemerkung}</p>`:''}<p style="margin-top:40px;color:#bbb;font-size:11px">Exportiert am ${new Date().toLocaleDateString('de-DE')} · Baumwächter</p></body></html>`);
    w.document.close(); w.print();
  };

  if (loading) return <Box><Skeleton height={80} /><Skeleton height={200} /><Skeleton height={300} /></Box>;
  if (error && !tree) return <Alert severity="error">{error}</Alert>;
  if (!tree) return null;

  const damages = tree.damages || [], measures = tree.measures || [], controls = tree.controls || [], photos = tree.photos || [];

  // FEAT-027/028: Letzte & nächste Kontrolle. Bevorzugt das explizit gespeicherte
  // „nächste Kontrolle"-Datum der jüngsten Kontrolle (FEAT-028 – risikobasierte
  // Importtermine, die nicht dem Intervall folgen); sonst aus letzter Kontrolle +
  // Kontroll-Intervall berechnet.
  const INTERVAL_MONTHS: Record<string, number> = { 'halbjährlich': 6, 'jährlich': 12, '2-jährlich': 24, '3-jährlich': 36 };
  const letzteKontrolleObj: any = (controls || [])
    .filter((c: any) => c.control_date)
    .sort((a: any, b: any) => String(a.control_date).localeCompare(String(b.control_date)))
    .slice(-1)[0] || null;
  const letzteKontrolle: string | null = letzteKontrolleObj?.control_date || null;
  const naechsteKontrolle: Date | null = letzteKontrolleObj?.naechste_kontrolle
    ? new Date(letzteKontrolleObj.naechste_kontrolle)
    : (letzteKontrolle
      ? (() => { const d = new Date(letzteKontrolle); d.setMonth(d.getMonth() + (INTERVAL_MONTHS[tree.kontroll_intervall || 'jährlich'] ?? 12)); return d; })()
      : null);
  const kontrolleUeberfaellig = naechsteKontrolle ? naechsteKontrolle < new Date(new Date().toDateString()) : false;
  // Eine Schaden-Prüfung ist gültig, bis das Kontrollintervall des Baums abläuft.
  const pruefIntervallMonate = INTERVAL_MONTHS[tree.kontroll_intervall || 'jährlich'] ?? 12;
  const istPruefungGueltig = (d: any) => {
    if (!d.bestaetigt_am) return false;
    const gueltigBis = new Date(d.bestaetigt_am);
    gueltigBis.setMonth(gueltigBis.getMonth() + pruefIntervallMonate);
    return gueltigBis.getTime() >= Date.now();
  };
  const offeneSchaeden = damages.filter((d: any) => d.status !== 'behoben' && d.status !== 'erledigt'
    && !istPruefungGueltig(d)).length;
  const offeneMassnahmen = measures.filter((m: any) => m.status !== 'erledigt' && m.status !== 'abgebrochen').length;
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

  return (
    <Box sx={{ pb: 2 }}>
      {/* HEADER */}
      <Box sx={{ mb: 2.5 }}>
        {/* Top-Toolbar: Zurück + Aktionen */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              if (cameFromLV) {
                navigate('/leistungsverzeichnis', {
                  state: { reopenLvId: fromState.lvId },
                });
              } else if (cameFromMap) {
                // Karte mit gleichem Ausschnitt wiederherstellen
                const params = new URLSearchParams();
                if (fromState.mapLat) params.set('lat', String(fromState.mapLat));
                if (fromState.mapLng) params.set('lng', String(fromState.mapLng));
                if (fromState.mapZoom) params.set('zoom', String(fromState.mapZoom));
                navigate(`/map${params.toString() ? '?' + params.toString() : ''}`);
              } else if (cameFromActivity) {
                // Zurück zur Historie mit erhaltenen Filtern (BUG-016)
                navigate('/activity', { state: { restoreFilters: true } });
              } else {
                navigate('/trees');
              }
            }}
            size="small"
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            {cameFromLV
              ? `Zurück zum LV${fromState.lvBezeichnung ? ` „${fromState.lvBezeichnung}"` : ''}`
              : cameFromMap
                ? 'Zurück zur Karte'
                : cameFromActivity
                  ? 'Zurück zur Historie'
                  : 'Zurück zur Liste'}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEdit && <Button startIcon={<Edit />} variant="contained" size="small" onClick={() => setEditDialog(true)}>Bearbeiten</Button>}
            {isAdmin && <IconButton size="small" color="error" onClick={handleDelete}><Delete fontSize="small" /></IconButton>}
          </Box>
        </Box>

        {/* Title-Block */}
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, lineHeight: 1.1, mb: 0.3 }}>
          {tree.is_baumgruppe ? 'Baumgruppe' : 'Baum'} {tree.baumnummer}
        </Typography>
        {tree.archive_baumnummer && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3, fontSize: '0.75rem' }}>
            Ursprünglich: <strong>{tree.archive_baumnummer}</strong> · gefällt{tree.gefaellt_a ? ` am ${new Date(tree.gefaellt_a).toLocaleDateString('de-DE')}` : ''}
          </Typography>
        )}
        {tree.is_baumgruppe && (
          <Box sx={{ mb: 0.5 }}>
            <Chip
              size="small"
              label={`Gruppe · ${tree.gruppe_anzahl || '?'} ${tree.gruppe_anzahl === 1 ? 'Baum' : 'Bäume'}`}
              color="primary"
              sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700 }}
            />
            {Array.isArray(tree.gruppe_arten) && tree.gruppe_arten.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: '0.78rem' }}>
                Arten: {tree.gruppe_arten.join(', ')}
              </Typography>
            )}
          </Box>
        )}
        <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1.5 }}>
          {tree.baumart || 'Unbekannte Baumart'}
        </Typography>

        {/* Meta + Status Chips in einer Zeile */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {tree.updated_at && (
            <Tooltip
              title={`${new Date(tree.updated_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr`}
              arrow
              placement="bottom-start"
            >
              <Chip
                icon={<Schedule sx={{ fontSize: 14 }} />}
                label={
                  <span>
                    Zuletzt geändert <strong>{getRelativeTime(tree.updated_at)}</strong>
                    {(tree.updated_by_name || tree.name_erf) && (
                      <> · von <strong>{tree.updated_by_name || tree.name_erf}</strong></>
                    )}
                  </span>
                }
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'primary.main',
                  color: 'text.primary',
                  fontWeight: 400,
                  fontSize: '0.78rem',
                  height: 26,
                  '& .MuiChip-icon': { color: 'primary.main' },
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Tooltip>
          )}
          {offeneSchaeden > 0 && (
            <Chip
              icon={<Warning sx={{ fontSize: 16 }} />}
              label={`${offeneSchaeden} ${offeneSchaeden === 1 ? 'Schaden' : 'Schäden'} offen`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 26, fontWeight: 600, fontSize: '0.78rem' }}
            />
          )}
          {offeneMassnahmen > 0 && (
            <Chip
              icon={<Star sx={{ fontSize: 16 }} />}
              label={`${offeneMassnahmen} ${offeneMassnahmen === 1 ? 'offene Maßnahme' : 'offene Maßnahmen'}`}
              size="small"
              color="info"
              variant="outlined"
              sx={{ height: 26, fontWeight: 600, fontSize: '0.78rem' }}
            />
          )}
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* GRUNDINFORMATIONEN — in drei logische Sektionen aufgeteilt (FEAT-017) */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
          {/* Identität & Standort */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 1 }}>
            Identität & Standort
          </Typography>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, px: 2, py: 0.5, mb: 2 }}>
          <Grid container spacing={0} columnSpacing={4}>
            <Grid item xs={12} md={6}>
              <InfoRow label="Baumart" value={tree.baumart} />
              <InfoRow label="Funktion" value={tree.grunflache} />
              <InfoRow label="Ort" value={tree.ort} />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoRow label="Straße" value={tree.strasse} />
              <InfoRow label="Eigentümer" value={tree.eigentueme} />
            </Grid>
          </Grid>
          </Box>

          {/* Bewertung */}
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 1 }}>
            Bewertung
          </Typography>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, px: 2, py: 0.5, mb: 2 }}>
          <Grid container spacing={0} columnSpacing={4}>
            <Grid item xs={12} md={6}>
              <InfoRow label="Zustand" value={tree.zustand} />
              <InfoRow label="Vitalität" value={tree.vitalitaet} />
              <InfoRow label="Entwicklung" value={tree.entwicklun} />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoRow label="Sicherheit" value={tree.sicherheit} />
              <InfoRow label="Verkehrssicherheit" value={
                tree.verkehrssicherheit
                  ? <Box component="span" sx={{ px: 1, py: 0.2, borderRadius: 1, fontWeight: 700, fontSize: '0.78rem', color: getVerkehrssicherheitColor(tree.verkehrssicherheit).color, bgcolor: getVerkehrssicherheitColor(tree.verkehrssicherheit).bg }}>{tree.verkehrssicherheit}</Box>
                  : '–'
              } />
              <InfoRow label="Kontroll-Intervall" value={tree.kontroll_intervall || 'jährlich'} />
              <InfoRow label="Letzte Kontrolle" value={letzteKontrolle ? new Date(letzteKontrolle).toLocaleDateString('de-DE') : '–'} />
              <InfoRow label="Nächste Kontrolle" value={
                <span style={{ color: kontrolleUeberfaellig ? '#d32f2f' : undefined, fontWeight: 600 }}>
                  {naechsteKontrolle ? naechsteKontrolle.toLocaleDateString('de-DE') : '–'}{kontrolleUeberfaellig ? ' · überfällig' : ''}
                </span>
              } />
            </Grid>
          </Grid>
          </Box>

          {/* Maße & Daten */}
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 1 }}>
            Maße & Daten
          </Typography>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, px: 2, py: 0.5 }}>
          <Grid container spacing={0} columnSpacing={4}>
            <Grid item xs={12} md={6}>
              <InfoRow label="Höhe" value={tree.hoehe ? `${tree.hoehe} m` : null} />
              <InfoRow label="Stammdurchmesser" value={tree.stdm ? `${tree.stdm} cm` : null} />
              <InfoRow label="Kronendurchmesser" value={tree.kdm ? `${tree.kdm} m` : null} />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoRow label="Stammhöhe" value={tree.sthoehe ? `${tree.sthoehe} m` : null} />
              <InfoRow label="Anzahl Stämme" value={tree.anzahlst} />
              <InfoRow label="Pflanzjahr" value={tree.pflanzjahr} />
            </Grid>
          </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* SCHÄDEN — Badge zählt nur offene/in Bearbeitung, behobene werden nicht mehr gezählt (FEAT-016) */}
      <Box sx={{ mb: 2.5, border: '1px solid', borderColor: offeneSchaeden > 0 ? alpha(theme.palette.warning.main, 0.5) : 'divider', borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, bgcolor: offeneSchaeden > 0 ? alpha(theme.palette.warning.main, isDark ? 0.18 : 0.12) : 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: 'warning.main', fontSize: 20 }} /><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Schäden</Typography>
            <Chip label={offeneSchaeden} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: offeneSchaeden > 0 ? 'warning.main' : 'action.disabled', color: 'white' }} />
            {damages.length > offeneSchaeden && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                · {damages.length - offeneSchaeden} behoben
              </Typography>
            )}
          </Box>
          {canReportDamage && <Button size="small" startIcon={<Add />} onClick={() => setDamageDialog(true)} sx={{ color: 'warning.main', fontWeight: 600, fontSize: '0.78rem' }}>Neuer Schaden</Button>}
        </Box>
        {damages.length === 0 ? (
          <EmptyState
            icon={HealingOutlined}
            title="Keine Schäden erfasst"
            description={canEdit ? "Sieht gut aus! Bei Bedarf kannst du oben einen neuen Schaden hinzufügen." : "Sieht gut aus! Aktuell sind keine Schäden erfasst."}
            size="small"
            accentColor="success"
          />
        ) : (
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {damages.map((d: any) => {
              const isR = d.status === 'behoben' || d.status === 'erledigt';
              // Geprüft & noch gültig (kein akuter Schaden) → neutral/grau darstellen.
              const geprueft = !isR && istPruefungGueltig(d);
              // Hat dieser Schaden bereits eine verknüpfte Maßnahme?
              const linkedMeasure = measures.find((m: any) => m.damage_id === d.id);
              return (
              <Box key={d.id} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid',
                borderColor: isR ? alpha(theme.palette.success.main, 0.4) : geprueft ? 'divider' : alpha(theme.palette.warning.main, 0.4),
                bgcolor: isR ? alpha(theme.palette.success.main, isDark ? 0.12 : 0.08) : geprueft ? 'action.hover' : alpha(theme.palette.warning.main, isDark ? 0.1 : 0.06),
                opacity: isR ? 0.75 : geprueft ? 0.85 : 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                  {d.location_on_tree && <Chip label={d.location_on_tree.toUpperCase()} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 800, bgcolor: alpha(theme.palette.info.main, isDark ? 0.2 : 0.12), color: 'info.main' }} />}
                  <Chip label={d.priority || 'Mittel'} size="small" color={PRIORITY_COLORS[d.priority] || 'info'} sx={{ height: 22, fontSize: '0.68rem' }} />
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">{d.discovered_date ? new Date(d.discovered_date).toLocaleDateString('de-DE') : d.created_at ? new Date(d.created_at).toLocaleDateString('de-DE') : ''}</Typography>
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>{d.damage_type || 'Schaden'}</Typography>
                {d.description && <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{d.description}</Typography>}
                {/* Ersteller-Zeile */}
                {d.reported_by_name && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                    Erfasst von: {d.reported_by_name}
                  </Typography>
                )}
                {d.bestaetigt_am && (() => {
                  const veraltet = !istPruefungGueltig(d);
                  return (
                    <Box sx={{ mt: 0.3 }}>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.72rem', color: veraltet ? 'warning.main' : 'success.main', fontWeight: 700 }}>
                        ✓ Geprüft von {d.bestaetigt_von_name || '–'} am {new Date(d.bestaetigt_am).toLocaleDateString('de-DE')}
                        {veraltet && ' · Prüfung veraltet'}
                      </Typography>
                      {d.bestaetigt_notiz && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.72rem', fontStyle: 'italic' }}>
                          „{d.bestaetigt_notiz}"
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
                {/* Verknüpfte Maßnahme anzeigen */}
                {linkedMeasure && (
                  <Chip
                    label={`Maßnahme: ${linkedMeasure.measure_type} (${linkedMeasure.status})`}
                    size="small"
                    icon={<Star sx={{ fontSize: 14 }} />}
                    sx={{ mt: 0.7, height: 22, fontSize: '0.68rem', bgcolor: alpha(theme.palette.info.main, 0.15), color: 'info.main' }}
                  />
                )}
                <Box sx={{ display: 'flex', gap: isMobile ? 0.6 : 0.8, alignItems: 'center', mt: 0.8, flexWrap: 'wrap' }}>
                  <Chip label={STATUS_LABELS[d.status] || d.status} size="small" color={STATUS_COLORS[d.status] || 'default'} sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600 }} />
                  {/* Primär – „Behoben" auch für Bauhof; „Geprüft" nur Erfasser/Admin */}
                  {canReportDamage && !isR && (
                    <Button size="small" variant="outlined" color="success" sx={{ fontSize: '0.72rem', py: 0, minHeight: isMobile ? 34 : 26 }} onClick={() => handleDamageStatus(d.id, 'behoben')}>✓ Behoben</Button>
                  )}
                  {canEdit && !isR && (
                    <Button size="small" variant="outlined" color="info" sx={{ fontSize: '0.72rem', py: 0, minHeight: isMobile ? 34 : 26 }} onClick={() => { setBestaetigenNotiz(d.bestaetigt_notiz || ''); setBestaetigenDialog(d.id); }}>
                      {d.bestaetigt_am ? (isMobile ? '✓ Erneut' : '✓ Erneut bestätigen') : (isMobile ? '✓ Geprüft' : '✓ Bestätigen (geprüft)')}
                    </Button>
                  )}
                  {/* Sekundär – Desktop inline, Mobile im ⋯-Menü */}
                  {!isMobile ? (
                    <>
                      {canEdit && !isR && d.status === 'offen' && <Button size="small" variant="outlined" sx={{ fontSize: '0.72rem', py: 0, minHeight: 26 }} onClick={() => handleDamageStatus(d.id, 'in_bearbeitung')}>In Bearbeitung</Button>}
                      {canEdit && d.bestaetigt_am && <Button size="small" variant="text" color="inherit" sx={{ fontSize: '0.68rem', py: 0, minHeight: 26 }} onClick={() => handleResetBestaetigung(d.id)}>Prüfung zurücksetzen</Button>}
                      {canEdit && !isR && !linkedMeasure && <Button size="small" variant="outlined" color="info" startIcon={<Add sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.72rem', py: 0, minHeight: 26 }} onClick={() => openMeasureFromDamage(d)}>Maßnahme dazu anlegen</Button>}
                      {isAdmin && <Button size="small" color="error" sx={{ fontSize: '0.72rem', py: 0, minHeight: 26, ml: 'auto' }} onClick={() => handleDeleteDamage(d.id)}>Löschen</Button>}
                    </>
                  ) : (
                    ((canEdit && !isR && d.status === 'offen') || (canEdit && !isR && !linkedMeasure) || (canEdit && d.bestaetigt_am) || isAdmin) && (
                      <IconButton size="small" onClick={(e) => setDamageMenu({ anchor: e.currentTarget, d })} sx={{ ml: 'auto' }}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    )
                  )}
                </Box>
              </Box>); })}
          </Box>
        )}
      </Box>

      {/* Mobiles ⋯-Menü für Schaden-Aktionen */}
      <Menu
        anchorEl={damageMenu?.anchor}
        open={Boolean(damageMenu)}
        onClose={() => setDamageMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {damageMenu && (() => {
          const d = damageMenu.d;
          const dIsR = d.status === 'behoben' || d.status === 'erledigt';
          const dLinked = measures.find((m: any) => m.damage_id === d.id);
          const items: any[] = [];
          if (canEdit && !dIsR && d.status === 'offen') items.push(
            <MenuItem key="ib" onClick={() => { setDamageMenu(null); handleDamageStatus(d.id, 'in_bearbeitung'); }}>
              <ListItemIcon><Schedule fontSize="small" /></ListItemIcon><ListItemText primary="In Bearbeitung" />
            </MenuItem>
          );
          if (canEdit && !dIsR && !dLinked) items.push(
            <MenuItem key="ma" onClick={() => { setDamageMenu(null); openMeasureFromDamage(d); }}>
              <ListItemIcon><Add fontSize="small" /></ListItemIcon><ListItemText primary="Maßnahme dazu anlegen" />
            </MenuItem>
          );
          if (canEdit && d.bestaetigt_am) items.push(
            <MenuItem key="rs" onClick={() => { setDamageMenu(null); handleResetBestaetigung(d.id); }}>
              <ListItemIcon><Close fontSize="small" /></ListItemIcon><ListItemText primary="Prüfung zurücksetzen" />
            </MenuItem>
          );
          if (isAdmin) items.push(
            <MenuItem key="del" onClick={() => { setDamageMenu(null); handleDeleteDamage(d.id); }} sx={{ color: 'error.main' }}>
              <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon><ListItemText primary="Löschen" />
            </MenuItem>
          );
          return items;
        })()}
      </Menu>

      {/* MASSNAHMEN */}
      <Box sx={{ mb: 2.5, border: '1px solid', borderColor: measures.length > 0 ? alpha(theme.palette.info.main, 0.5) : 'divider', borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, bgcolor: measures.length > 0 ? alpha(theme.palette.info.main, isDark ? 0.18 : 0.12) : 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Star sx={{ color: 'info.main', fontSize: 20 }} /><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Maßnahmen</Typography><Chip label={measures.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: measures.length > 0 ? 'info.main' : 'action.disabled', color: 'white' }} /></Box>
          {canEdit && <Button size="small" startIcon={<Add />} onClick={() => { setMeasureError(''); setMeasureDialog(true); }} sx={{ color: 'info.main', fontWeight: 600, fontSize: '0.78rem' }}>Neue Maßnahme</Button>}
        </Box>
        {measures.length === 0 ? (
          <EmptyState
            icon={EventNote}
            title="Keine Maßnahmen geplant"
            description={canEdit ? "Plane oben eine neue Maßnahme wie Rückschnitt, Düngung oder Fällung." : "Aktuell sind keine Maßnahmen geplant."}
            size="small"
            accentColor="info"
          />
        ) : (
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {measures.map((m: any) => {
              const isDone = m.status === 'erledigt';
              const inProgress = m.status === 'in Arbeit';
              const isCancel = m.status === 'abgebrochen';
              // Statusfarbe + Label
              const statusColor: 'success' | 'info' | 'warning' | 'default' = isDone ? 'success' : inProgress ? 'warning' : isCancel ? 'default' : 'info';
              const statusLabel = isDone ? '✓ Erledigt' : inProgress ? '⟳ In Arbeit' : isCancel ? '⊘ Abgebrochen' : 'Offen';
              return (
              <Box key={m.id} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: isDone ? alpha(theme.palette.success.main, 0.4) : inProgress ? alpha(theme.palette.warning.main, 0.4) : alpha(theme.palette.info.main, 0.4), bgcolor: isDone ? alpha(theme.palette.success.main, isDark ? 0.12 : 0.08) : inProgress ? alpha(theme.palette.warning.main, isDark ? 0.12 : 0.08) : alpha(theme.palette.info.main, isDark ? 0.1 : 0.06), opacity: isDone || isCancel ? 0.75 : 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                  <Chip label={m.measure_type || 'Maßnahme'} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 800, bgcolor: alpha(theme.palette.success.main, isDark ? 0.2 : 0.12), color: 'success.main' }} />
                  <Chip label={statusLabel} size="small" color={statusColor} sx={{ height: 22, fontSize: '0.68rem' }} />
                  <Box sx={{ flex: 1 }} />
                  {m.planned_date && (
                    <Typography variant="caption" color="text.secondary">
                      Geplant: {new Date(m.planned_date).toLocaleDateString('de-DE')}
                    </Typography>
                  )}
                  {m.planned_date && m.completed_date && (
                    <Typography variant="caption" color="text.secondary">·</Typography>
                  )}
                  {m.completed_date && (
                    <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                      Erledigt: {new Date(m.completed_date).toLocaleDateString('de-DE')}
                    </Typography>
                  )}
                </Box>
                {m.description && <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{m.description}</Typography>}
                {/* Verknüpfter Schaden anzeigen */}
                {m.damage_id && (m.linked_damage_type || m.linked_damage_location) && (
                  <Chip
                    label={`Aus Schaden: ${m.linked_damage_type || 'Schaden'}${m.linked_damage_location ? ` (${m.linked_damage_location})` : ''}`}
                    size="small"
                    icon={<Warning sx={{ fontSize: 14 }} />}
                    sx={{ mt: 0.7, height: 22, fontSize: '0.68rem', bgcolor: alpha(theme.palette.warning.main, 0.15), color: 'warning.main' }}
                  />
                )}
                {/* Ersteller + Bearbeiter-Zeile */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                  {m.created_by_name && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Erfasst: {m.created_by_name}
                    </Typography>
                  )}
                  {m.assigned_to_name && (
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: m.assigned_to === user?.id ? 'primary.main' : 'text.secondary', fontWeight: m.assigned_to === user?.id ? 700 : 400 }}>
                      Bearbeiter: {m.assigned_to_name}{m.assigned_to === user?.id ? ' (du)' : ''}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', mt: 0.8, flexWrap: 'wrap' }}>
                  {canEdit && !isDone && !isCancel && m.status === 'offen' && (
                    <Button size="small" variant="outlined" color="warning" sx={{ fontSize: '0.72rem', py: 0, minHeight: 26 }} onClick={() => handleMeasureStatus(m.id, 'in Arbeit')}>
                      In Arbeit nehmen
                    </Button>
                  )}
                  {canEdit && !isDone && !isCancel && (
                    <Button size="small" variant="contained" color="success" sx={{ fontSize: '0.72rem', py: 0, minHeight: 26 }} onClick={() => handleMeasureStatus(m.id, 'erledigt')}>
                      Als erledigt markieren
                    </Button>
                  )}
                  {canEdit && <Button size="small" variant="outlined" sx={{ fontSize: '0.72rem', py: 0, minHeight: 26 }} onClick={() => openEditMeasure(m)}>Bearbeiten</Button>}
                  {isAdmin && <Button size="small" color="error" sx={{ fontSize: '0.72rem', py: 0, minHeight: 26, ml: 'auto' }} onClick={() => handleDeleteMeasure(m.id)}>Löschen</Button>}
                </Box>
              </Box>); })}
          </Box>
        )}
      </Box>

      {/* BEMERKUNGEN */}
      {tree.bemerkung && <Card sx={{ mb: 2.5 }}><CardContent sx={{ pb: '12px !important', pt: 1.5 }}><Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 1 }}>Bemerkungen</Typography><Box sx={{ borderLeft: '3px solid #1976d2', pl: 1.5, py: 0.5 }}><Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{tree.bemerkung}</Typography></Box></CardContent></Card>}

      {/* FOTOS */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CameraAlt sx={{ color: 'text.secondary', fontSize: 20 }} /><Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 1 }}>Fotos ({photos.length})</Typography></Box>
            {canEdit && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={uploading ? undefined : <PhotoCamera sx={{ fontSize: 18 }} />}
                  onClick={(e) => {
                    if (isMobile) {
                      // Mobile: Menü mit Kamera / Galerie öffnen
                      setPhotoMenuAnchor(e.currentTarget);
                    } else {
                      // Desktop: direkt Datei-Picker
                      fileInputRef.current?.click();
                    }
                  }}
                  disabled={uploading}
                  sx={{ bgcolor: '#4a7c59', fontSize: '0.78rem', '&:hover': { bgcolor: '#3a6c49' } }}
                >
                  {uploading ? <CircularProgress size={16} color="inherit" /> : 'Foto hinzufügen'}
                </Button>

                {/* Versteckte File-Inputs */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={handlePhotoUpload}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={handlePhotoUpload}
                />

                {/* Mobile Auswahl-Menü */}
                <Menu
                  anchorEl={photoMenuAnchor}
                  open={Boolean(photoMenuAnchor)}
                  onClose={() => setPhotoMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={() => { setPhotoMenuAnchor(null); cameraInputRef.current?.click(); }}>
                    <ListItemIcon><PhotoCamera fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Kamera" secondary="Foto direkt aufnehmen" />
                  </MenuItem>
                  <MenuItem onClick={() => { setPhotoMenuAnchor(null); fileInputRef.current?.click(); }}>
                    <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Galerie" secondary="Aus Bibliothek wählen" />
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
          {photos.length === 0 ? (
            <EmptyState
              icon={PhotoLibrary}
              title="Noch keine Fotos"
              description={canEdit ? "Lade Fotos vom Baum hoch – auf Mobile direkt mit der Kamera." : "Noch keine Fotos vorhanden."}
              size="small"
              accentColor="primary"
            />
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {photos.map((p: any) => (
                <Box key={p.id} sx={{ position: 'relative', width: 140, height: 140, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                  <img src={`${API_BASE}${p.file_path}`} alt={p.filename} style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} onClick={() => window.open(`${API_BASE}${p.file_path}`, '_blank')} />
                  {isAdmin && <IconButton size="small" onClick={() => handleDeletePhoto(p.id)} sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)', width: 24, height: 24 }}><Close sx={{ fontSize: 14, color: 'error.main' }} /></IconButton>}
                </Box>))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* KONTROLLEN */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: '#4a7c59', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 1 }}>Kontrollen ({controls.length})</Typography>
            </Box>
            {canEdit && <Button size="small" variant="contained" startIcon={<Add />} onClick={openControlDialog} sx={{ bgcolor: '#4a7c59', fontSize: '0.78rem', '&:hover': { bgcolor: '#3a6c49' } }}>Neue Kontrolle</Button>}
          </Box>
          {controls.length === 0 ? (
            <EmptyState
              icon={FactCheck}
              title="Noch keine Kontrollen"
              description={canEdit ? "Erfasse die erste Sichtkontrolle für diesen Baum." : "Für diesen Baum wurden noch keine Kontrollen durchgeführt."}
              size="small"
              accentColor="success"
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {controls.map((c: any) => (
                <Box key={c.id} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={`Kontrolle ${c.control_number || ''}`} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 800, bgcolor: alpha(theme.palette.success.main, isDark ? 0.2 : 0.12), color: 'success.main' }} />
                      {c.zustand && <Chip label={c.zustand} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.68rem' }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary">{c.control_date ? new Date(c.control_date).toLocaleDateString('de-DE') : '–'}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Kontrolleur: {c.controller_name || '–'}</Typography>
                  {c.handlungsbedarf && <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'warning.main', mt: 0.3 }}>Handlungsbedarf: {c.handlungsbedarf}</Typography>}
                  {c.durchfuehrungszeitraum && <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.3 }}>Durchführungszeitraum: {c.durchfuehrungszeitraum}</Typography>}
                  {c.control_text && <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{c.control_text}</Typography>}
                  <Box sx={{ display: 'flex', gap: 0.8, mt: 0.8 }}>
                    <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} sx={{ fontSize: '0.72rem', py: 0, minHeight: 26 }}
                      onClick={() => generateControlPDF(c, tree)}>PDF</Button>
                    {isAdmin && <Button size="small" color="error" sx={{ fontSize: '0.72rem', py: 0, minHeight: 26 }}
                      onClick={async () => { if (!window.confirm('Kontrolle löschen?')) return; try { await api.delete(`/inspections/${c.id}`); loadTree(); } catch {} }}>Löschen</Button>}
                  </Box>
                </Box>))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* BOTTOM ACTIONS */}
      <Box sx={{ mt: 3, mb: 2, pt: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button startIcon={<PictureAsPdf />} variant="outlined" size="small" onClick={handlePDFExport}>PDF Report</Button>
        {tree.latitude && tree.longitude && <Button startIcon={<MapIcon />} variant="contained" size="small" onClick={() => navigate(`/map?lat=${tree.latitude}&lng=${tree.longitude}&id=${tree.id}`)}>Auf Karte anzeigen</Button>}
      </Box>

      {/* ═══ EDIT DIALOG ═══ */}
      <Dialog open={editDialog} onClose={() => { setEditDialog(false); setEditFromControl(false); }} maxWidth="md" fullWidth>
        <DialogTitle>Baum bearbeiten</DialogTitle>
        <DialogContent>
          {/* Hinweis bei Wechsel zu 'gefällt' (FEAT-014) */}
          {editData.zustand === 'gefällt' && tree.zustand !== 'gefällt' && !tree.gefaellt && (
            <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
              <strong>Achtung:</strong> Beim Speichern wird die Baumnummer <strong>{tree.baumnummer}</strong> freigegeben
              und der Baum bekommt eine neue GF-Nummer (z.B. GF000001). Die ursprüngliche Nummer wird im Archiv vermerkt.
            </Alert>
          )}

          {/* Baumgruppen-Toggle (FEAT-015) */}
          <Box sx={{
            mt: 1, mb: 1, p: 1.5, borderRadius: 1.5,
            border: '1px solid', borderColor: editData.is_baumgruppe ? 'primary.main' : 'divider',
            bgcolor: editData.is_baumgruppe ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!editData.is_baumgruppe}
                  onChange={(_, checked) => setEditData({ ...editData, is_baumgruppe: checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>Das ist eine Baumgruppe</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                    Mehrere kleine Bäume die dicht beieinanderstehen
                  </Typography>
                </Box>
              }
              sx={{ m: 0, alignItems: 'flex-start' }}
            />
            {editData.is_baumgruppe && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  size="small"
                  type="number"
                  label="Anzahl Bäume *"
                  value={editData.gruppe_anzahl || ''}
                  onChange={e => setEditData({ ...editData, gruppe_anzahl: e.target.value ? parseInt(e.target.value) : null })}
                  sx={{ width: { xs: '100%', sm: 140 } }}
                  inputProps={{ min: 2 }}
                />
                <Autocomplete
                  multiple freeSolo size="small"
                  options={autocompleteOptions.baumarten}
                  value={editData.gruppe_arten || []}
                  onChange={(_, v) => setEditData({ ...editData, gruppe_arten: v as string[] })}
                  sx={{ flex: 1 }}
                  renderTags={(value, getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={index} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label="Arten (mehrere möglich)" placeholder="Eiche, Linde..." />}
                />
              </Box>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* ── Identität & Standort ────────────────────────────────────── */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem', display: 'block', mt: 1 }}>
                Identität & Standort
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth label="Baumnummer" value={editData.baumnummer || ''} size="small" InputLabelProps={{ shrink: true }}
                onChange={e => setEditData({ ...editData, baumnummer: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={5}>
              <Autocomplete
                freeSolo size="small"
                options={autocompleteOptions.baumarten}
                value={editData.baumart || ''}
                onChange={(_, v) => setEditData({ ...editData, baumart: v || '' })}
                onInputChange={(_, v) => setEditData({ ...editData, baumart: v })}
                renderInput={(params) => <TextField {...params} label="Baumart" InputLabelProps={{ shrink: true }} />}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Funktion</InputLabel>
                <Select label="Funktion" value={editData.grunflache || ''} displayEmpty
                  onChange={e => setEditData({ ...editData, grunflache: e.target.value })}>
                  <MenuItem value=""><em>– keine Angabe –</em></MenuItem>
                  {FUNKTION_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={4}>
              <Autocomplete
                freeSolo size="small"
                options={autocompleteOptions.orte}
                value={editData.ort || ''}
                onChange={(_, v) => setEditData({ ...editData, ort: v || '' })}
                onInputChange={(_, v) => setEditData({ ...editData, ort: v })}
                renderInput={(params) => <TextField {...params} label="Ort" InputLabelProps={{ shrink: true }} />}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Autocomplete
                freeSolo size="small"
                options={autocompleteOptions.strassen}
                value={editData.strasse || ''}
                onChange={(_, v) => setEditData({ ...editData, strasse: v || '' })}
                onInputChange={(_, v) => setEditData({ ...editData, strasse: v })}
                renderInput={(params) => <TextField {...params} label="Straße" InputLabelProps={{ shrink: true }} />}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Autocomplete
                freeSolo size="small"
                options={autocompleteOptions.eigentuemer}
                value={editData.eigentueme || ''}
                onChange={(_, v) => setEditData({ ...editData, eigentueme: v || '' })}
                onInputChange={(_, v) => setEditData({ ...editData, eigentueme: v })}
                renderInput={(params) => <TextField {...params} label="Eigentümer" InputLabelProps={{ shrink: true }} />}
              />
            </Grid>

            {/* ── Bewertung ──────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem', display: 'block', mt: 1.5 }}>
                Bewertung
              </Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Zustand</InputLabel>
                <Select label="Zustand" value={editData.zustand || ''} displayEmpty onChange={e => setEditData({ ...editData, zustand: e.target.value })}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {ZUSTAND_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Vitalität</InputLabel>
                <Select label="Vitalität" value={editData.vitalitaet || ''} displayEmpty onChange={e => setEditData({ ...editData, vitalitaet: e.target.value })}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {VITALITAET_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Entwicklung</InputLabel>
                <Select label="Entwicklung" value={editData.entwicklun || ''} displayEmpty onChange={e => setEditData({ ...editData, entwicklun: e.target.value })}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {ENTWICKLUNG_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Sicherheit</InputLabel>
                <Select label="Sicherheit" value={editData.sicherheit || ''} displayEmpty onChange={e => setEditData({ ...editData, sicherheit: e.target.value })}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {SICHERHEIT_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Verkehrssicherheit</InputLabel>
                <Select label="Verkehrssicherheit" value={editData.verkehrssicherheit || ''} displayEmpty onChange={e => setEditData({ ...editData, verkehrssicherheit: e.target.value })}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {VERKEHRSSICHERHEIT_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Kontroll-Intervall</InputLabel>
                <Select label="Kontroll-Intervall" value={editData.kontroll_intervall || ''} displayEmpty onChange={e => setEditData({ ...editData, kontroll_intervall: e.target.value })}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {KONTROLL_INTERVALL_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* ── Maße & Daten ───────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem', display: 'block', mt: 1.5 }}>
                Maße & Daten
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth label="Höhe (m)" type="number" InputLabelProps={{ shrink: true }} placeholder="z.B. 12"
                value={editData.hoehe || ''} size="small" onChange={e => setEditData({ ...editData, hoehe: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth label="Stammdurchm. (cm)" type="number" InputLabelProps={{ shrink: true }} placeholder="z.B. 35"
                value={editData.stdm || ''} size="small" onChange={e => setEditData({ ...editData, stdm: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth label="Kronendurchm. (m)" type="number" InputLabelProps={{ shrink: true }} placeholder="z.B. 8"
                value={editData.kdm || ''} size="small" onChange={e => setEditData({ ...editData, kdm: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth label="Stammhöhe (m)" type="number" InputLabelProps={{ shrink: true }} placeholder="z.B. 2.5"
                value={editData.sthoehe || ''} size="small" onChange={e => setEditData({ ...editData, sthoehe: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Anz. Stämme</InputLabel>
                <Select label="Anz. Stämme" value={editData.anzahlst || ''} displayEmpty onChange={e => setEditData({ ...editData, anzahlst: e.target.value })}>
                  <MenuItem value=""><em>– bitte wählen –</em></MenuItem>
                  {ANZAHL_STAEMME_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth label="Pflanzjahr" type="number" InputLabelProps={{ shrink: true }} placeholder="z.B. 1985"
                value={editData.pflanzjahr || ''} size="small" onChange={e => setEditData({ ...editData, pflanzjahr: e.target.value })} />
            </Grid>

            {/* ── Bemerkungen ────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <TextField fullWidth label="Bemerkungen" multiline rows={3} InputLabelProps={{ shrink: true }}
                placeholder="Zusatzinformationen, Besonderheiten, Hinweise..."
                value={editData.bemerkung || ''} size="small"
                onChange={e => setEditData({ ...editData, bemerkung: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={()=>{ setEditDialog(false); setEditFromControl(false); }}>Abbrechen</Button><Button onClick={handleSaveEdit} variant="contained" disabled={saving}>Speichern</Button></DialogActions>
      </Dialog>

      {/* ═══ DAMAGE DIALOG ═══ */}
      <Dialog open={damageDialog} onClose={() => setDamageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}><Warning /> Schaden hinzufügen</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>✦ Schadbereich</Typography><FormControl fullWidth size="small"><Select value={damageForm.schadbereich} displayEmpty onChange={e => setDamageForm({ ...damageForm, schadbereich: e.target.value, damage_type: '' })}><MenuItem value="" disabled>Bitte wählen...</MenuItem>{Object.keys(SCHAD_BEREICHE).map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}</Select></FormControl></Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>+ Schadmerkmal</Typography>
              <FormControl fullWidth size="small" disabled={!damageForm.schadbereich}>
                <Select
                  value={damageForm.damage_type}
                  displayEmpty
                  onChange={e => {
                    const newType = e.target.value;
                    // Welcher Bereich-Key in unserem Helper? (BUG-021)
                    const bereichKey: 'krone' | 'stamm' | 'stammfuss' =
                      damageForm.schadbereich === 'Krone' ? 'krone' :
                      damageForm.schadbereich === 'Stamm' ? 'stamm' :
                      'stammfuss';
                    // Auto-Fill: Beschreibung übernimmt FLL-Text, wenn leer
                    // oder noch alte Beschreibung drin
                    const prevBeschr = damageForm.damage_type
                      ? getMerkmalBeschreibung(bereichKey, damageForm.damage_type)
                      : '';
                    const newBeschr = getMerkmalBeschreibung(bereichKey, newType);
                    const cur = damageForm.description || '';
                    const shouldFill = cur.trim() === '' || cur.trim() === prevBeschr.trim();
                    setDamageForm({
                      ...damageForm,
                      damage_type: newType,
                      description: shouldFill ? newBeschr : cur,
                    });
                  }}
                  renderValue={(v) => (v as string) || (damageForm.schadbereich ? 'Bitte wählen...' : 'Erst Schadbereich wählen')}
                >
                  <MenuItem value="" disabled>{damageForm.schadbereich ? 'Bitte wählen...' : 'Erst Schadbereich wählen'}</MenuItem>
                  {schadmerkmale.map(m => {
                    const bereichKey: 'krone' | 'stamm' | 'stammfuss' =
                      damageForm.schadbereich === 'Krone' ? 'krone' :
                      damageForm.schadbereich === 'Stamm' ? 'stamm' :
                      'stammfuss';
                    const beschr = getMerkmalBeschreibung(bereichKey, m);
                    const showBeschr = beschr !== m;
                    return (
                      <MenuItem key={m} value={m} sx={{ py: 0.7 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{m}</Typography>
                          {showBeschr && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                              {beschr}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>☆ Schweregrad</Typography><FormControl fullWidth size="small"><Select value={damageForm.severity} onChange={e => setDamageForm({ ...damageForm, severity: e.target.value })}><MenuItem value="Gering">Gering</MenuItem><MenuItem value="Mittel">Mittel</MenuItem><MenuItem value="Hoch">Hoch</MenuItem><MenuItem value="Sehr hoch">Sehr hoch</MenuItem></Select></FormControl></Box>
            <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>◉ Ort am Baum (Optional)</Typography><TextField fullWidth size="small" placeholder="z.B. Nordseite, 3m Höhe..." value={damageForm.location_on_tree} onChange={e => setDamageForm({ ...damageForm, location_on_tree: e.target.value })} /></Box>
            <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>📋 Beschreibung (Optional)</Typography><TextField fullWidth size="small" multiline rows={3} placeholder="Details..." value={damageForm.description} onChange={e => setDamageForm({ ...damageForm, description: e.target.value })} /></Box>
            <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>📅 Entdeckungsdatum</Typography><TextField fullWidth size="small" type="date" value={damageForm.discovered_date} onChange={e => setDamageForm({ ...damageForm, discovered_date: e.target.value })} /></Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'action.hover', px: 3, py: 1.5 }}><Button variant="contained" onClick={handleCreateDamage} disabled={saving || !damageForm.schadbereich} sx={{ bgcolor: '#4a7c59' }}>Schaden speichern</Button><Button variant="outlined" onClick={() => setDamageDialog(false)}>Abbrechen</Button></DialogActions>
      </Dialog>

      {/* ═══ SCHADEN BESTÄTIGEN (GEPRÜFT) DIALOG ═══ */}
      <Dialog open={!!bestaetigenDialog} onClose={() => setBestaetigenDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Schaden bestätigen (geprüft)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Der Schaden bleibt als Schaden gelistet, wird aber als <strong>geprüft</strong> markiert (mit deinem Namen und dem heutigen Datum) und zählt nicht mehr als offener Handlungsbedarf. Optional ein Prüfvermerk – z.B. warum keine Maßnahme nötig ist.
          </Typography>
          <TextField autoFocus fullWidth multiline rows={3} size="small" label="Prüfvermerk (optional)"
            placeholder="z.B. keine Gefährdung, da abseits des Weges – wird beobachtet"
            value={bestaetigenNotiz} onChange={(e) => setBestaetigenNotiz(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'action.hover', px: 3, py: 1.5 }}>
          <Button variant="outlined" onClick={() => setBestaetigenDialog(null)}>Abbrechen</Button>
          <Button variant="contained" color="success" onClick={submitBestaetigung}>Als geprüft bestätigen</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ MEASURE DIALOG ═══ */}
      <Dialog open={measureDialog} onClose={() => { setMeasureDialog(false); setMeasureError(''); setEditingMeasureId(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4a7c59', color: 'white', py: 1.5 }}>{editingMeasureId ? 'Maßnahme bearbeiten' : '+ Maßnahme hinzufügen'}</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Fehler/Duplikat-Hinweis */}
            {measureError && (
              <Alert severity="warning" onClose={() => setMeasureError('')} sx={{ fontSize: '0.85rem' }}>
                {measureError}
              </Alert>
            )}
            {/* Hinweis bei verknüpftem Schaden */}
            {measureForm.damage_id && (
              <Alert severity="info" icon={<Warning />} sx={{ fontSize: '0.85rem' }}>
                Diese Maßnahme wird mit dem ausgewählten Schaden verknüpft.
              </Alert>
            )}
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>+ Maßnahmentyp</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={measureForm.measure_type}
                  displayEmpty
                  onChange={e => {
                    const newType = e.target.value;
                    // Auto-Fill: Beim Wechsel wird die FLL-Beschreibung in die Beschreibung
                    // übernommen – aber nur wenn die Beschreibung leer ist ODER noch
                    // die Beschreibung des vorherigen Typs drin steht (BUG-021)
                    const prevBeschr = getMeasureBeschreibung(measureForm.measure_type);
                    const newBeschr  = getMeasureBeschreibung(newType);
                    const cur = measureForm.description || '';
                    const shouldFill =
                      cur.trim() === '' ||
                      cur.trim() === prevBeschr.trim() ||
                      (measureForm.measure_type === '' && cur.trim() === '');
                    setMeasureForm({
                      ...measureForm,
                      measure_type: newType,
                      description: shouldFill ? newBeschr : cur,
                    });
                  }}
                  renderValue={(v) => v as string || 'Bitte wählen...'}
                >
                  <MenuItem value="" disabled>Bitte wählen...</MenuItem>
                  {MEASURE_TYPE_GROUPS.flatMap((group) => [
                    <ListSubheader key={`hdr-${group.label}`} sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', lineHeight: '28px', bgcolor: 'background.paper' }}>
                      {group.label}
                    </ListSubheader>,
                    ...group.items.map(t => (
                      <MenuItem key={t.value} value={t.value} sx={{ pl: 3, py: 0.7 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{t.value}</Typography>
                          {t.beschreibung !== t.value && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                              {t.beschreibung}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
            </Box>
            <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>📋 Beschreibung (Optional)</Typography><TextField fullWidth size="small" multiline rows={3} placeholder="Details..." value={measureForm.description} onChange={e => setMeasureForm({ ...measureForm, description: e.target.value })} /></Box>
            <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>📅 Geplantes Datum</Typography><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} value={measureForm.planned_date} onChange={e => setMeasureForm({ ...measureForm, planned_date: e.target.value })} /></Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>👤 Bearbeiter (Optional)</Typography>
              <FormControl fullWidth size="small">
                <Select value={measureForm.assigned_to} displayEmpty onChange={e => setMeasureForm({ ...measureForm, assigned_to: e.target.value })}>
                  <MenuItem value=""><em>Niemand zugewiesen</em></MenuItem>
                  {user && <MenuItem value={user.id}>Mir selbst zuweisen ({user.name})</MenuItem>}
                  {users.filter((u: any) => u.id !== user?.id).map((u: any) => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>⊙ Status</Typography><FormControl fullWidth size="small"><Select value={measureForm.status} onChange={e => setMeasureForm({ ...measureForm, status: e.target.value })}><MenuItem value="offen">Offen</MenuItem><MenuItem value="in Arbeit">In Arbeit</MenuItem><MenuItem value="erledigt">Erledigt</MenuItem><MenuItem value="abgebrochen">Abgebrochen</MenuItem></Select></FormControl></Box>
            <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>€ Kosten (optional)</Typography><TextField fullWidth size="small" type="number" inputProps={{ min: 0, step: '0.01' }} placeholder="0,00" value={measureForm.cost} onChange={e => setMeasureForm({ ...measureForm, cost: e.target.value })} /></Box>
            {(measureForm.status === 'erledigt' || measureForm.status === 'abgebrochen') && (
              <>
                <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>✓ Erledigt am</Typography><TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} value={measureForm.completed_date} onChange={e => setMeasureForm({ ...measureForm, completed_date: e.target.value })} /></Box>
                <Box><Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>📝 Abschlussnotizen (optional)</Typography><TextField fullWidth size="small" multiline rows={2} value={measureForm.completion_notes} onChange={e => setMeasureForm({ ...measureForm, completion_notes: e.target.value })} /></Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'action.hover', px: 3, py: 1.5 }}><Button variant="contained" onClick={handleCreateMeasure} disabled={saving || !measureForm.measure_type} sx={{ bgcolor: '#4a7c59' }}>{editingMeasureId ? 'Änderungen speichern' : 'Maßnahme speichern'}</Button><Button variant="outlined" onClick={() => { setMeasureDialog(false); setEditingMeasureId(null); setMeasureForm(EMPTY_MEASURE_FORM); }}>Abbrechen</Button></DialogActions>
      </Dialog>

      {/* Bestätigungs-Dialog: Schaden mit-erledigen? (FEAT-012) */}
      <Dialog open={!!confirmDamageBehoben} onClose={() => confirmAndCloseDamage(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Schaden als behoben markieren?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Die Maßnahme wurde erledigt. Soll der verknüpfte Schaden ebenfalls als „behoben" markiert werden?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => confirmAndCloseDamage(false)}>Nein, offen lassen</Button>
          <Button variant="contained" color="success" onClick={() => confirmAndCloseDamage(true)}>Ja, behoben</Button>
        </DialogActions>
      </Dialog>

      {/* FEAT-029: Maßnahmen-Vorschlag bei "nicht verkehrssicher" */}
      <Dialog open={!!suggestMeasure} onClose={() => setSuggestMeasure(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Maßnahme anlegen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Der Baum wurde als <strong>nicht verkehrssicher</strong> eingestuft. Möchtest du dafür direkt eine Maßnahme anlegen? Der Befund wird als Beschreibung übernommen.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuggestMeasure(null)}>Nein, danke</Button>
          <Button variant="contained" sx={{ bgcolor: '#4a7c59' }} onClick={() => {
            const desc = suggestMeasure?.description || '';
            setSuggestMeasure(null);
            setEditingMeasureId(null);
            setMeasureForm({ ...EMPTY_MEASURE_FORM, description: desc });
            setMeasureError('');
            setMeasureDialog(true);
          }}>Maßnahme anlegen</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ FLL KONTROLLE DIALOG ═══ */}
      <Dialog open={controlDialog} onClose={() => setControlDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4a7c59', color: 'white', display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <Assignment /> Baumkontrolle nach FLL-Richtlinien
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>

            {/* Datum */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>📅 Kontrolldatum</Typography>
              <TextField size="small" type="date" value={controlForm.control_date}
                onChange={e => setControlForm({ ...controlForm, control_date: e.target.value })} />
            </Box>

            <Divider sx={{ my: 1 }}><Chip label="Baummaße" size="small" /></Divider>
            <Grid container spacing={2}>
              <Grid item xs={4}><TextField fullWidth size="small" label="Baumhöhe (m)" type="number" InputLabelProps={{ shrink: true }} placeholder="z.B. 12" value={controlForm.baumhoehe} onChange={e => setControlForm({ ...controlForm, baumhoehe: e.target.value })} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" label="Stamm-Ø (cm)" type="number" InputLabelProps={{ shrink: true }} placeholder="z.B. 35" value={controlForm.stammdurchmesser} onChange={e => setControlForm({ ...controlForm, stammdurchmesser: e.target.value })} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" label="Kronen-Ø (m)" type="number" InputLabelProps={{ shrink: true }} placeholder="z.B. 8" value={controlForm.kronendurchmesser} onChange={e => setControlForm({ ...controlForm, kronendurchmesser: e.target.value })} /></Grid>
            </Grid>

            <Divider sx={{ my: 1.5 }}><Chip label="Bewertung" size="small" /></Divider>

            <RadioSelect label="Entwicklungsphase" value={controlForm.entwicklungsphase}
              options={['Jugendphase', 'Reifephase', 'Alterungsphase', 'Abgangsphase']}
              onChange={v => setControlForm({ ...controlForm, entwicklungsphase: v })} />

            <RadioSelect label="Vitalitätsstufe" value={controlForm.vitalitaetsstufe}
              options={['0 – vital', '1 – leicht geschwächt', '2 – geschwächt', '3 – stark geschwächt']}
              onChange={v => setControlForm({ ...controlForm, vitalitaetsstufe: v })} />

            <RadioSelect label="Zustand" value={controlForm.zustand}
              options={['gut', 'leichte Mängel', 'erhebliche Mängel', 'Gefahr im Verzug']}
              onChange={v => setControlForm({ ...controlForm, zustand: v })} />

            {/* Kontroll-Intervall: read-only Anzeige aus trees.kontroll_intervall (Single Source).
                Edit-Button öffnet das Baum-Bearbeiten-Dialog. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.3 }}>Kontrollintervall</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {tree.kontroll_intervall || 'jährlich'} <Typography component="span" variant="caption" color="text.secondary">(am Baum festgelegt)</Typography>
                </Typography>
              </Box>
              {canEdit && (
                <Button size="small" variant="outlined" startIcon={<Edit sx={{ fontSize: 14 }} />}
                  onClick={() => { setControlDialog(false); setEditFromControl(true); setEditDialog(true); }}
                  sx={{ fontSize: '0.75rem' }}>
                  Am Baum bearbeiten
                </Button>
              )}
            </Box>

            <RadioSelect label="Sicherheitserwartung" value={controlForm.sicherheitserwartung}
              options={['hoch', 'niedrig']}
              onChange={v => setControlForm({ ...controlForm, sicherheitserwartung: v })} />

            <Divider sx={{ my: 1.5 }}><Chip label="Defektsymptome & Verkehrssicherheit" size="small" /></Divider>

            {/* Krone */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4a7c59', mb: 1 }}>🌿 Krone</Typography>
              {/* FLL-Schadmerkmale Multi-Select (BUG-018) – Beschreibung wird beim Auswählen
                  in den Befund-Text vor-ausgefüllt (BUG-019) */}
              <Autocomplete
                multiple size="small"
                options={SCHADMERKMAL_KRONE_OPTIONS}
                value={controlForm.krone_merkmale || []}
                onChange={(_, v) => handleMerkmalChange('krone', controlForm.krone_merkmale || [], v, 'krone_merkmale', 'krone_befund')}
                renderOption={renderMerkmalOption('krone')}
                renderInput={(params) => <TextField {...params} label="Schadmerkmale (FLL)" placeholder="Mehrere möglich..." InputLabelProps={{ shrink: true }} />}
                sx={{ mb: 1.5 }}
              />
              <TextField fullWidth size="small" label="Befund (Freitext)" multiline rows={3} InputLabelProps={{ shrink: true }} placeholder="Details: Höhe, Seite, Maße – Platzhalter xx ersetzen" sx={{ mb: 1.5 }}
                value={controlForm.krone_befund} onChange={e => setControlForm({ ...controlForm, krone_befund: e.target.value })} />
              <RadioSelect label="Verkehrssicherheit" value={controlForm.krone_verkehr}
                options={['gegeben', 'nicht gegeben', 'eingeschränkt gegeben']}
                onChange={v => setControlForm({ ...controlForm, krone_verkehr: v })} />
            </Box>

            {/* Stamm */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4a7c59', mb: 1 }}>🪵 Stamm</Typography>
              <Autocomplete
                multiple size="small"
                options={SCHADMERKMAL_STAMM_OPTIONS}
                value={controlForm.stamm_merkmale || []}
                onChange={(_, v) => handleMerkmalChange('stamm', controlForm.stamm_merkmale || [], v, 'stamm_merkmale', 'stamm_befund')}
                renderOption={renderMerkmalOption('stamm')}
                renderInput={(params) => <TextField {...params} label="Schadmerkmale (FLL)" placeholder="Mehrere möglich..." InputLabelProps={{ shrink: true }} />}
                sx={{ mb: 1.5 }}
              />
              <TextField fullWidth size="small" label="Befund (Freitext)" multiline rows={3} InputLabelProps={{ shrink: true }} placeholder="Details: Höhe, Seite, Maße – Platzhalter xx ersetzen" sx={{ mb: 1.5 }}
                value={controlForm.stamm_befund} onChange={e => setControlForm({ ...controlForm, stamm_befund: e.target.value })} />
              <RadioSelect label="Verkehrssicherheit" value={controlForm.stamm_verkehr}
                options={['gegeben', 'nicht gegeben', 'eingeschränkt gegeben']}
                onChange={v => setControlForm({ ...controlForm, stamm_verkehr: v })} />
            </Box>

            {/* Wurzeln / Stammfuß */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mb: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4a7c59', mb: 1 }}>🌱 Stammfuß / Wurzeln / Baumumfeld</Typography>
              <Autocomplete
                multiple size="small"
                options={SCHADMERKMAL_STAMMFUSS_OPTIONS}
                value={controlForm.wurzeln_merkmale || []}
                onChange={(_, v) => handleMerkmalChange('stammfuss', controlForm.wurzeln_merkmale || [], v, 'wurzeln_merkmale', 'wurzeln_befund')}
                renderOption={renderMerkmalOption('stammfuss')}
                renderInput={(params) => <TextField {...params} label="Schadmerkmale (FLL)" placeholder="Mehrere möglich..." InputLabelProps={{ shrink: true }} />}
                sx={{ mb: 1.5 }}
              />
              <TextField fullWidth size="small" label="Befund (Freitext)" multiline rows={3} InputLabelProps={{ shrink: true }} placeholder="Details: Seite, Bodenzustand – Platzhalter xx ersetzen" sx={{ mb: 1.5 }}
                value={controlForm.wurzeln_befund} onChange={e => setControlForm({ ...controlForm, wurzeln_befund: e.target.value })} />
              <RadioSelect label="Verkehrssicherheit" value={controlForm.wurzeln_verkehr}
                options={['gegeben', 'nicht gegeben', 'eingeschränkt gegeben']}
                onChange={v => setControlForm({ ...controlForm, wurzeln_verkehr: v })} />
            </Box>

            <Divider sx={{ my: 1.5 }}><Chip label="Ergebnis" size="small" /></Divider>

            <Box sx={{ mb: 1.5 }}>
              <RadioSelect label="Verkehrssicherheit (Gesamtergebnis)" value={controlForm.verkehrssicherheit}
                options={VERKEHRSSICHERHEIT_OPTIONS}
                onChange={v => setControlForm({ ...controlForm, verkehrssicherheit: v })} />
              <Typography variant="caption" color="text.secondary">Wird beim Speichern als Verkehrssicherheit am Baum übernommen.</Typography>
            </Box>

            <TextField fullWidth size="small" label="Handlungsbedarf / Empfehlungen" multiline rows={3}
              value={controlForm.handlungsbedarf} onChange={e => setControlForm({ ...controlForm, handlungsbedarf: e.target.value })} />

            <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
              <InputLabel>Durchführungszeitraum</InputLabel>
              <Select label="Durchführungszeitraum" value={controlForm.durchfuehrungszeitraum}
                onChange={e => setControlForm({ ...controlForm, durchfuehrungszeitraum: e.target.value })}>
                <MenuItem value=""><em>– keine Angabe –</em></MenuItem>
                {['Unverzüglich', '2 Wochen', '4 Wochen', '6 Wochen', '12 Wochen', 'Bis zur nächsten Regelkontrolle']
                  .map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField fullWidth size="small" label="Weitere Bemerkungen" multiline rows={2} sx={{ mt: 1.5 }}
              value={controlForm.control_text} onChange={e => setControlForm({ ...controlForm, control_text: e.target.value })} />

          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'action.hover', px: 3, py: 1.5 }}>
          <Button variant="contained" onClick={handleCreateControl} disabled={saving}
            sx={{ bgcolor: '#4a7c59', '&:hover': { bgcolor: '#3a6c49' } }}>
            Kontrolle speichern
          </Button>
          <Button variant="outlined" onClick={() => { setControlDialog(false); resetControlForm(); }}>Abbrechen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
